/**
 * Data Transformation Utilities
 * 
 * Centralized utilities for transforming API responses into form-friendly data structures.
 * Moves data mapping logic out of form components and into dedicated utility functions.
 */

// ============================================================================
// Type Definitions
// ============================================================================

export interface SelectOption {
  value: string
  label: string
}

export interface DataTransformConfig {
  valueField: string
  labelField: string
  additionalFields?: Record<string, string>
}

// ============================================================================
// Data Transformation Functions
// ============================================================================

/**
 * Transform API data array into select options
 * 
 * Immediately after API fetch, transform the response data into
 * the format expected by select controls.
 * 
 * @param data - Array of objects from API response
 * @param config - Configuration specifying which fields to map
 * @returns Array of SelectOption objects suitable for select controls
 * 
 * @example
 * ```typescript
 * const manufacturerOptions = transformMapForListBox(
 *   manufacturersData?.manufacturers || [],
 *   {
 *     valueField: 'manufacturerNumber',
 *     labelField: 'manufacturerName'
 *   }
 * )
 * ```
 */
export function transformMapForListBox<T extends Record<string, any>>(
  data: T[],
  config: DataTransformConfig
): SelectOption[] {
  if (!data || !Array.isArray(data)) {
    return []
  }

  return data.map((item) => ({
    value: String(item[config.valueField]),
    label: String(item[config.labelField]),
  }))
}

/**
 * Transform device type data with optional model information
 * 
 * @param data - Array of device type objects from API
 * @returns Array of SelectOption objects with device type name and model
 * 
 * @example
 * ```typescript
 * const deviceTypeOptions = transformDeviceTypesForListBox(
 *   deviceTypesData?.deviceTypes || []
 * )
 * ```
 */
export function transformDeviceTypesForListBox(
  data: Array<{ deviceTypeNumber: number; deviceTypeName: string; deviceTypeModel?: string }>
): SelectOption[] {
  if (!data || !Array.isArray(data)) {
    return []
  }

  return data.map((item) => ({
    value: String(item.deviceTypeNumber),
    label: `${item.deviceTypeName}${item.deviceTypeModel ? ` (${item.deviceTypeModel})` : ''}`,
  }))
}

/**
 * Filter and transform device types by manufacturer
 * 
 * @param deviceTypes - Array of device types from API
 * @param manufacturerNumber - Manufacturer ID to filter by (optional)
 * @returns Array of filtered and transformed SelectOption objects
 * 
 * @example
 * ```typescript
 * const filteredDeviceTypes = filterDeviceTypesByManufacturer(
 *   deviceTypesData?.deviceTypes || [],
 *   selectedManufacturer
 * )
 * ```
 */
export function filterDeviceTypesByManufacturer(
  deviceTypes: Array<{ 
    deviceTypeNumber: number
    deviceTypeName: string
    deviceTypeModel?: string
    deviceTypeManufacturerNumber: number
  }>,
  manufacturerNumber?: number
): SelectOption[] {
  if (!deviceTypes || !Array.isArray(deviceTypes)) {
    return []
  }

  const filtered = manufacturerNumber
    ? deviceTypes.filter((dt) => dt.deviceTypeManufacturerNumber === manufacturerNumber)
    : deviceTypes

  return transformDeviceTypesForListBox(filtered)
}

/**
 * Find an item in an API response array by ID
 * 
 * Useful for cascading dropdowns where selecting one field
 * needs to update another based on a relationship.
 * 
 * @param data - Array of items from API response
 * @param idField - Name of the ID field to match
 * @param idValue - Value to match
 * @returns The matched item or undefined
 * 
 * @example
 * ```typescript
 * const selectedType = findItemById(
 *   deviceTypesData?.deviceTypes || [],
 *   'deviceTypeNumber',
 *   selectedDeviceTypeNumber
 * )
 * ```
 */
export function findItemById<T extends Record<string, any>>(
  data: T[],
  idField: keyof T,
  idValue: any
): T | undefined {
  if (!data || !Array.isArray(data)) {
    return undefined
  }

  return data.find((item) => String(item[idField]) === String(idValue))
}
